import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ArrowLeft,
  LogOut,
  Package,
  Heart,
  MapPin,
  CreditCard,
  HelpCircle,
  User
} from "lucide-react";
import GatewayMyOrder from "../gateway/MyOrder";

const getBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const backendRoot = process.env.REACT_APP_BACKEND;
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  return (backendUrl || apiBase || backendRoot || "http://localhost:8000").replace(/\/$/, "");
};

const withApiPrefix = (baseUrl) =>
  baseUrl.includes("/api/v1") ? baseUrl : `${baseUrl}/api/v1`;

const getUserIdFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return (
      decoded?.id ||
      decoded?.user_id ||
      decoded?.customer_id ||
      decoded?.customerId ||
      decoded?.userId ||
      null
    );
  } catch {
    return null;
  }
};

const AccountPage = () => {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab");
  const [active, setActive] = useState(initialTab || "menu");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [stats, setStats] = useState({
    orders: 0,
    wishlist: 0,
    addresses: 0,
    points: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);


  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab && tab !== active) {
      setActive(tab);
    }
  }, [location.search, active]);

  useEffect(() => {
    const saved = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!saved && !token) {
      navigate("/login?redirect=/profile");
      return;
    }

    try {
      const parsed = saved ? JSON.parse(saved) : {};
      const tokenId = (() => {
        if (!token) return null;
        try {
          const payload = token.split(".")[1];
          const decoded = JSON.parse(atob(payload));
          return (
            decoded?.id ||
            decoded?.user_id ||
            decoded?.customer_id ||
            decoded?.customerId ||
            decoded?.userId ||
            null
          );
        } catch {
          return null;
        }
      })();
      const merged = {
        ...parsed,
        id: parsed?.id || tokenId
      };
      if (merged?.id && merged?.id !== parsed?.id) {
        localStorage.setItem("user", JSON.stringify(merged));
      }
      setUser(merged);

      if (merged?.id && token) {

        setLoadingProfile(true);
        const baseUrl = getBaseUrl();
        fetch(`${baseUrl}/customers/one/${merged.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || "Profile fetch failed");
            return data;
          })
          .then((data) => {
            setProfileData(data);
            setEditForm({
              name: data?.name || "",
              address: data?.address || "",
            });
          })
          .catch(() => {
            setProfileData(parsed);
            setEditForm({
              name: parsed?.name || "",
              address: parsed?.address || "",
            });
          })
          .finally(() => setLoadingProfile(false));
      }
    } catch {
      navigate("/login?redirect=/profile");
    }
  }, [navigate]);

  useEffect(() => {
    const source = profileData || user || {};
    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const countOf = (value) => {
      if (Array.isArray(value)) return value.length;
      return toNumber(value);
    };

    const wishlistCount = countOf(
      source.wishlist ?? source.wishlists ?? source.wishlist_items ?? source.wishlistItems
    );
    const addressesCount = countOf(
      source.addresses ?? source.address_list ?? source.addressList
    );
    const pointsCount = toNumber(
      source.points ?? source.reward_points ?? source.rewardPoints ?? source.reward
    );
    const ordersCount = toNumber(
      source.totalOrders ?? source.order_count ?? source.ordersCount ?? source.orderCount
    );

    setStats((prev) => ({
      ...prev,
      wishlist: wishlistCount ?? 0,
      addresses: addressesCount ?? (source.address ? 1 : 0),
      points: pointsCount ?? 0,
      orders: ordersCount ?? prev.orders ?? 0,
    }));
  }, [profileData, user]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId =
      profileData?.id ||
      profileData?._id ||
      user?.id ||
      user?._id ||
      user?.customer_id ||
      user?.user_id ||
      user?.customerId ||
      user?.userId ||
      getUserIdFromToken(token);

    if (!token || !userId) return;

    setStatsLoading(true);
    const baseUrl = withApiPrefix(getBaseUrl());
    const headers = { Authorization: `Bearer ${token}` };

    const fetchOrdersCount = async () => {
      let res = await fetch(`${baseUrl}/order/${userId}`, { headers });
      if (!res.ok) {
        res = await fetch(`${baseUrl}/orderlist/${userId}`, { headers });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to fetch orders (${res.status}).`);
      }

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.orders)
            ? data.orders
            : [];

      setStats((prev) => ({ ...prev, orders: list.length }));
    };

    fetchOrdersCount()
      .catch(() => {
        // keep existing stats on failure
      })
      .finally(() => setStatsLoading(false));
  }, [profileData, user]);

  const handleSave = async () => {
    setSaveMsg("");
    if (!user?.id) {
      setSaveMsg("User not found. Please login again.");
      return;
    }

    if (!editForm.name && !editForm.address) {
      setSaveMsg("Please update at least one field.");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const baseUrl = getBaseUrl();

      const res = await fetch(`${baseUrl}/customers/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editForm.name,
          address: editForm.address,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Update failed");
      }

      const updated = data?.data || {
        ...profileData,
        name: editForm.name,
        address: editForm.address,
      };

      setProfileData(updated);
      const mergedUser = { ...user, name: updated?.name, address: updated?.address };
      setUser(mergedUser);
      localStorage.setItem("user", JSON.stringify(mergedUser));

      setSaveMsg("Profile updated successfully.");
    } catch (err) {
      setSaveMsg(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("userUpdated"));
    navigate("/login");
  };

  const profile = {
    name: profileData?.name || user?.name || user?.email || "User",
    mobile: profileData?.mobile || user?.mobile || "N/A",
  };

  const menuItems = [
    { key: "profile", label: "Profile", icon: User },
    { key: "orders", label: "My Orders", icon: Package },
    { key: "help", label: "Help Center", icon: HelpCircle },
  ];

 return (
  <div className="min-h-screen bg-[#F4F6FB]">
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">
      <div className="flex flex-col md:flex-row gap-6">

        {/* ================= SIDEBAR ================= */}
        <div
          className={`md:w-80 w-full ${
            active !== "menu" ? "hidden md:block" : ""
          }`}
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

            {/* Top Gradient Line */}
            <div className="h-28 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500"></div>

  {/* Profile Card */}
  <div className="px-6 pb-6 -mt-16">
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 text-center">

      {/* Avatar */}
      <div className="flex justify-center">
        <div className="w-24 h-24 rounded-full border-4 border-white shadow-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-center justify-center text-3xl font-bold">
          {profileData?.name?.charAt(0) || "U"}
        </div>
      </div>

      {/* Name */}
      <h2 className="mt-4 text-lg font-semibold text-gray-800">
        {profileData?.name || "User Name"}
      </h2>

      {/* Mobile */}
      <p className="text-sm text-gray-500 mt-1">
        {profileData?.mobile || "No Mobile"}
      </p>

      {/* Email */}
      <p className="text-sm text-gray-500 mt-1 break-words">
        {profileData?.email || "No Email Added"}
      </p>

      {/* Divider */}
      <div className="my-4 border-t border-gray-100"></div>

      {/* Extra Info Section */}
      <div className="space-y-2 text-sm text-gray-600">
        <p>
          <span className="font-medium text-gray-800">Address:</span>{" "}
          {profileData?.address || "Not Added"}
        </p>

        
      </div>

    </div>
  </div>

            {/* Menu Items */}
            <div className="p-3 space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => setActive(item.key)}
                  className={`group w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition ${
                    active === item.key
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon
                      size={18}
                      className={`${
                        active === item.key
                          ? "text-indigo-600"
                          : "text-gray-400 group-hover:text-indigo-500"
                      }`}
                    />
                    {item.label}
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </button>
              ))}
            </div>

            {/* Logout */}
            <div className="border-t p-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 transition"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>

          </div>
        </div>

        {/* ================= CONTENT ================= */}
        {/* ================= CONTENT ================= */}
<div className="flex-1 w-full">

  {/* Mobile Back Button */}
  {active !== "menu" && (
    <div className="md:hidden mb-4">
      <button
        onClick={() => setActive("menu")}
        className="flex items-center gap-2 text-indigo-600 font-medium"
      >
        <ArrowLeft size={18} />
        Back
      </button>
    </div>
  )}

  {/* ===== DASHBOARD (Default Desktop) ===== */}
  {active === "menu" && (
    <div className="hidden md:block  ">

      <h1 className="text-2xl font-semibold text-gray-800 mb-6">
        Welcome Back 👋
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-indigo-50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">Total Orders</p>
          <h3 className="text-xl font-semibold text-indigo-600 mt-1">
            {statsLoading ? "..." : stats.orders}
          </h3>
        </div>

        {/* <div className="bg-pink-50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">Wishlist</p>
          <h3 className="text-xl font-semibold text-pink-600 mt-1">
            {loadingProfile ? "..." : stats.wishlist}
          </h3>
        </div> */}

        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">Saved Addresses</p>
          <h3 className="text-xl font-semibold text-green-600 mt-1">
            {loadingProfile ? "..." : stats.addresses}
          </h3>
        </div>

        <div className="bg-yellow-50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">Reward Points</p>
          <h3 className="text-xl font-semibold text-yellow-600 mt-1">
            {loadingProfile ? "..." : stats.points}
          </h3>
        </div>
      </div>

    </div>
  )}

  {/* ===== OTHER PAGES ===== */}
  {active !== "menu" && (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 md:p-3">

      {active === "profile" ? (
        <ProfileContent
          loadingProfile={loadingProfile}
          profileData={profileData}
          user={user}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={saving}
          saveMsg={saveMsg}
          onSave={handleSave}
        />
      ) : active === "orders" ? (
        <GatewayMyOrder />
      ) : active === "help" ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Help Center</h2>
          <p className="text-sm text-gray-600">
            Need assistance? Contact PC Care using the details below.
          </p>
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-gray-800 space-y-2">
            <p>
              Email:{" "}
              <a
                href="mailto:info@pccarecomputers.com"
                className="text-indigo-700 font-semibold hover:underline"
              >
                info@pccarecomputers.com
              </a>
            </p>
            <p>
              Phone:{" "}
              <a
                href="tel:+919238264606"
                className="text-indigo-700 font-semibold hover:underline"
              >
                +91 9238264606
              </a>
            </p>
            <p>
              Address:{" "}
              <a
                href="https://www.google.com/maps/search/?api=1&query=Pc%20Care%20Computer%2C%201035%20Malviya%20Chowk%2C%20Infront%20Of%20Yatayat%20Thana%2C%20Jabalpur%20(Mp)%20482001"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-700 font-semibold hover:underline"
              >
                Pc Care Computer, 1035 Malviya Chowk, Infront Of Yatayat Thana, Jabalpur (Mp) 482001
              </a>
            </p>
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-lg font-semibold mb-4 capitalize">
            {active}
          </h2>
          <p className="text-gray-600 text-sm">
            This is your {active} section content.
          </p>
        </>
      )}

    </div>
  )}

</div>


      </div>
    </div>
  </div>
);

};

const ProfileContent = ({
  loadingProfile,
  profileData,
  user,
  editForm,
  setEditForm,
  saving,
  saveMsg,
  onSave,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">My Profile</h2>
        <p className="text-sm text-gray-500">
          Update your name and address from here.
        </p>
      </div>

      {loadingProfile ? (
        <p className="text-sm text-gray-500">Loading profile...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600">Name</label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="w-full mt-1 p-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Mobile</label>
            <input
              type="text"
              value={profileData?.mobile || user?.mobile || ""}
              readOnly
              className="w-full mt-1 p-2.5 border rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Email</label>
            <input
              type="text"
              value={profileData?.email || user?.email || ""}
              readOnly
              className="w-full mt-1 p-2.5 border rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Address</label>
            <input
              type="text"
              value={editForm.address}
              onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              className="w-full mt-1 p-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
        </div>
      )}

      {saveMsg && (
        <div className="text-sm text-center text-gray-700">{saveMsg}</div>
      )}

      <div className="flex justify-start">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

const DashboardCard = ({ title, value }) => {
  return (
    <div className="bg-indigo-50 rounded-xl p-4 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer">
      <p className="text-lg font-bold text-indigo-700">{value}</p>
      <p className="text-xs text-gray-600 mt-1">{title}</p>
    </div>
  );
};

const OrdersContent = ({ user, profileData }) => {
  return <GatewayMyOrder />;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getUserIdFromToken = (token) => {
    if (!token) return null;
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return (
        decoded?.id ||
        decoded?.user_id ||
        decoded?.customer_id ||
        decoded?.customerId ||
        decoded?.userId ||
        null
      );
    } catch {
      return null;
    }
  };

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const backendRoot = process.env.REACT_APP_BACKEND;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    return (backendUrl || apiBase || backendRoot || "http://localhost:8000").replace(/\/$/, "");
  };

  useEffect(() => {
    const fetchOrders = () => {
      const savedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (!savedUser && !token) {
        navigate("/login?redirect=/profile");
        return;
      }

      const storedUser = savedUser ? JSON.parse(savedUser) : {};
      const userId =
        profileData?.id ||
        profileData?._id ||
        storedUser?.id ||
        storedUser?._id ||
        storedUser?.customer_id ||
        storedUser?.user_id ||
        storedUser?.customerId ||
        storedUser?.userId ||
        getUserIdFromToken(token);
      if (!userId) {
        setLoading(false);
        setError("User ID not found. Please log in again.");
        return;
      }

      setLoading(true);
      const baseUrl = withApiPrefix(getBaseUrl());
      if (!token) {
        setLoading(false);
        setError("Authorization token missing. Please login again.");
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };

      const fetchUserOrders = async () => {
        let res = await fetch(`${baseUrl}/order/${userId}`, { headers });
        if (!res.ok) {
          res = await fetch(`${baseUrl}/orderlist/${userId}`, { headers });
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.message || `Failed to fetch orders (${res.status}).`);
        }
        return data;
      };

      fetchUserOrders()
        .then((data) => {
          setOrders(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    };

    fetchOrders();
  }, [navigate]);

  const formatDeliveryAddress = (order) => {
    const parts = [order.shipping_address, order.flatno, order.landmark, order.city, order.state, order.pincode];
    return parts.filter(Boolean).join(", ");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">My Orders</h2>
        <p className="text-sm text-gray-500">
          Here is a list of your past orders.
        </p>
      </div>

      {loading ? (
        <p>Loading your orders...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : orders.length === 0 ? (
        <p>You have not placed any orders yet.</p>
      ) : (
        orders.map((order) => (
          <div
            key={order.id}
            className="border border-gray-200 rounded-2xl"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-sm text-gray-500">Order ID</p>
                <p className="text-base font-semibold text-gray-800">
                  {order.order_id}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700">
                  {order.status || 'Processing'}
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">
                  {order.payment_method} - Paid
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Delivery Address</p>
                  <p className="text-sm text-gray-800">{formatDeliveryAddress(order)}</p>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Order Total</span>
                  <span className="font-semibold text-gray-800">
                    ₹{order.total_amount}
                  </span>
                </div>
                <button className="w-full mt-1 px-3 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 transition">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AccountPage;
